/** @format */

/** CTA/PGDA year-of-study id (matches YearOfStudy seed). */
export const PGDA_YEAR_OF_STUDY_ID = 5;

/** Bursary application for undergraduate 2nd / 3rd year (YearOfStudy seed ids). */
export const SECOND_YEAR_BURSARY_YEAR_OF_STUDY_ID = 2;
export const THIRD_YEAR_BURSARY_YEAR_OF_STUDY_ID = 3;

/** Fourth-year undergraduate bursary (matches YearOfStudy seed id). */
export const FOURTH_YEAR_BURSARY_YEAR_OF_STUDY_ID = 4;

export function isSecondOrThirdYearBursary(yearOfStudyId: number): boolean {
  return (
    yearOfStudyId === SECOND_YEAR_BURSARY_YEAR_OF_STUDY_ID ||
    yearOfStudyId === THIRD_YEAR_BURSARY_YEAR_OF_STUDY_ID
  );
}

/**
 * HDI universities (seed `Institution`): Zululand, Venda, University of Limpopo.
 * Used for education year-of-study rules and 4th-year dual-university choice on Study Details.
 */
export const EDUCATION_HDI_INSTITUTION_IDS_ORDERED = [23, 3, 30] as const;

/** Coerce MUI Select / API values so HDI checks work when id is stored as string. */
export function toInstitutionId(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function isEducationHdi(institutionId: unknown): boolean {
  const id = toInstitutionId(institutionId);
  if (id <= 0) return false;
  return (EDUCATION_HDI_INSTITUTION_IDS_ORDERED as readonly number[]).includes(
    id
  );
}

/** True when 4th year should not be greyed out (HDI + Walter Sisulu historical rule). */
export function isFourthYearOfStudySelectable(institutionId: unknown): boolean {
  const id = toInstitutionId(institutionId);
  return isEducationHdi(id) || id === 25;
}

/** HDI ids for multiselect ordering: current institution first, then the others. */
export function orderedEducationHdiIdsForEducation(
  educationInstitutionId: unknown
): number[] {
  const id = toInstitutionId(educationInstitutionId);
  const ordered = EDUCATION_HDI_INSTITUTION_IDS_ORDERED as readonly number[];
  if (!isEducationHdi(id)) {
    return [...ordered];
  }
  const rest = ordered.filter((oid) => oid !== id);
  return [id, ...rest];
}

/**
 * HDI universities for PGDA selection, in business order:
 * 1) University of Zululand (UniZulu), 2) University of Venda (UniVen).
 * Matches Institution seed ids.
 */
export const PGDA_HDI_INSTITUTION_IDS_ORDERED = [23, 3] as const;

export function isPgdaHdiApplicant(educationInstitutionId: unknown): boolean {
  const id = toInstitutionId(educationInstitutionId);
  if (id <= 0) return false;
  return (PGDA_HDI_INSTITUTION_IDS_ORDERED as readonly number[]).includes(id);
}

/**
 * Study step "year applying for" is 4th year at UniZulu or UniVen: same business rule as 2nd/3rd year
 * (applicant may only list their current tertiary institution).
 */
export function isUniZuluOrUniVenFourthYearSingleInstitution(
  studyYearOfStudyId: number,
  educationInstitutionId: unknown
): boolean {
  return (
    studyYearOfStudyId === FOURTH_YEAR_BURSARY_YEAR_OF_STUDY_ID &&
    isPgdaHdiApplicant(educationInstitutionId)
  );
}

/** 2nd / 3rd year, or 4th year at University of Zululand / University of Venda: locked to current institution only. */
export function isSingleCurrentInstitutionStudyLock(
  studyYearOfStudyId: number,
  educationInstitutionId: unknown
): boolean {
  if (toInstitutionId(educationInstitutionId) <= 0) return false;
  return (
    isSecondOrThirdYearBursary(studyYearOfStudyId) ||
    isUniZuluOrUniVenFourthYearSingleInstitution(
      studyYearOfStudyId,
      educationInstitutionId
    )
  );
}

/** HDI ids for the multiselect: current institution first, then the other HDI. */
export function orderedPgdaHdiIdsForEducation(
  educationInstitutionId: unknown
): number[] {
  const id = toInstitutionId(educationInstitutionId);
  const rest = (PGDA_HDI_INSTITUTION_IDS_ORDERED as readonly number[]).filter(
    (oid) => oid !== id
  );
  if (!isPgdaHdiApplicant(id)) {
    return [...PGDA_HDI_INSTITUTION_IDS_ORDERED];
  }
  return [id, ...rest];
}
